import { useState, useCallback, useMemo, useEffect } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  AreaChart,
  Area,
} from "recharts";
import { RemoteDataSource } from "@/datasources/remote_datasource";
import { AppConfig } from "@/datasources/AppConfig";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import {
  Loader2,
  MapPin,
  X,
  BarChart3,
  Calendar,
  FileText,
  CheckCircle2,
  AlertTriangle,
  Timer,
  CalendarCheck,
  ShieldAlert,
  Ban,
  Circle,
} from "lucide-react";
import { useAgenteAtualStore } from "@/stores/agenteAtualStore";
import { useCurrentOuvidoriaStore } from "@/stores/currentOuvidoriaStore";
import { ptBR } from "date-fns/locale";

const api = new RemoteDataSource("https://api.toolbox.app.br/urbnet");

interface BairroData {
  name: string;
  value: number;
  color: string;
}

interface Endereco {
  bairro?: string;
  cidade?: string;
  logradouro?: string;
  estado?: string;
}

interface SolicitacaoFull {
  id: string;
  protocolo: string;
  status: string;
  estadoAprovacao: string;
  arquivada: boolean;
  dataCriacao: string;
  dataConclusao?: string;
  totalApoiadores: number;
  topico: { id: string; descricao: string; etiquetas?: string[] };
  endereco?: Endereco;
  agenteResponsavel?: { id: string; nome: string; fotoPerfil?: { id: string; arquivo: string } };
}

interface Solicitacao {
  id: string;
  protocolo: string;
  status: string;
  estadoAprovacao: string;
  arquivada: boolean;
  dataCriacao: string;
  dataConclusao?: string;
  totalApoiadores: number;
  topico: { id: string; descricao: string; etiquetas?: string[] };
  endereco?: Endereco;
  agenteResponsavel?: AgenteCompacto;
}

interface AgenteCompacto {
  id: string;
  nome: string;
  ouvidoria?: { id: string; nome: string };
  gestor?: boolean;
  status?: string;
  arquivado?: boolean;
  fotoPerfil?: { id: string; arquivo: string };
}

interface Topico {
  id: string;
  descricao: string;
  etiquetas: string[];
}

function normalize(str: string): string {
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

const PIE_COLORS = [
  "hsl(var(--success))",
  "hsl(var(--destructive))",
  "hsl(var(--info))",
  "hsl(var(--primary))",
  "hsl(var(--secondary))",
  "hsl(var(--tertiary))",
  "hsl(var(--warning))",
  "hsl(var(--accent))",
];

// Agrega contagem por chave string
function aggregate(items: string[]): { name: string; value: number }[] {
  const map: Record<string, number> = {};
  for (const item of items) {
    const key = item || "Não informado";
    map[key] = (map[key] || 0) + 1;
  }
  return Object.entries(map)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
}

// Chave de data local (YYYY-MM-DD) — evita problemas de timezone com toISOString()
function toLocalDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Agrupa solicitações por dia — de hoje retroativamente pelo período
function buildTimelineFor(
  solicitacoes: Solicitacao[],
  days: number,
  predicate: (s: Solicitacao) => boolean,
): { name: string; value: number }[] {
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  const points: { name: string; value: number }[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const dayStr = toLocalDateKey(d);
    const label = d.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    const value = solicitacoes.filter((s) => {
      if (!s.dataCriacao) return false;
      return toLocalDateKey(new Date(s.dataCriacao)) === dayStr && predicate(s);
    }).length;
    points.push({ name: label, value });
  }
  return points;
}

// Agrupa solicitações por dia — para intervalo personalizado
function buildTimelineForRange(
  solicitacoes: Solicitacao[],
  fromDate: Date,
  toDate: Date,
  predicate: (s: Solicitacao) => boolean,
): { name: string; value: number }[] {
  const points: { name: string; value: number }[] = [];
  const start = new Date(fromDate);
  start.setHours(12, 0, 0, 0);
  const end = new Date(toDate);
  end.setHours(12, 0, 0, 0);
  const diffTime = end.getTime() - start.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  for (let i = 0; i < diffDays; i++) {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    const dayStr = toLocalDateKey(d);
    const label = d.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    const value = solicitacoes.filter((s) => {
      if (!s.dataCriacao) return false;
      return toLocalDateKey(new Date(s.dataCriacao)) === dayStr && predicate(s);
    }).length;
    points.push({ name: label, value });
  }
  return points;
}

// Agrupa solicitações por mês/ano — usado no filtro "Todo o tempo"
function buildTimelineAllTimeByMonth(
  solicitacoes: Solicitacao[],
  predicate: (s: Solicitacao) => boolean,
): { name: string; value: number }[] {
  const map: Record<string, number> = {};
  const sorted = [...solicitacoes].sort(
    (a, b) => new Date(a.dataCriacao).getTime() - new Date(b.dataCriacao).getTime(),
  );
  for (const s of sorted) {
    if (!s.dataCriacao || !predicate(s)) continue;
    const d = new Date(s.dataCriacao);
    const key = `${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
    map[key] = (map[key] || 0) + 1;
  }
  return Object.entries(map).map(([name, value]) => ({ name, value }));
}

const statusMatch = (raw: string) => (s: Solicitacao) =>
  normalize(s.status) === normalize(raw);

const aprovacaoMatch = (raw: string) => (s: Solicitacao) =>
  normalize(s.estadoAprovacao) === normalize(raw);

export default function GraphsPage() {
  const { ouvidoria: currentOuvidoria, fetchCurrentOuvidoria } = useCurrentOuvidoriaStore();
  const isGeralOuvidoria = currentOuvidoria?.geral ?? true;

  useEffect(() => {
    fetchCurrentOuvidoria();
  }, [fetchCurrentOuvidoria]);

  const [search, setSearch] = useState("");
  const [bairros, setBairros] = useState<BairroData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filtro de tempo (mesma mecânica do DashboardPage)
  const [periodo, setPeriodo] = useState("7d");
  const [showDateModal, setShowDateModal] = useState(false);
  const [showYearModal, setShowYearModal] = useState(false);
  const [dateRange, setDateRange] = useState<{ from?: Date; to?: Date }>({});
  const [appliedDateRange, setAppliedDateRange] = useState<{ from?: Date; to?: Date }>({});
  const [ano, setAno] = useState<number | undefined>(undefined);
  const [appliedAno, setAppliedAno] = useState<number | undefined>(undefined);
  const [fromMonth, setFromMonth] = useState<Date>(new Date());
  const [toMonth, setToMonth] = useState<Date>(new Date());
  const [solicitacoesFull, setSolicitacoesFull] = useState<SolicitacaoFull[]>([]);
  const [loadingSolicitacoes, setLoadingSolicitacoes] = useState(false);
  const [solicitacoes, setSolicitacoes] = useState<Solicitacao[]>([]);
  const [topicosMap, setTopicosMap] = useState<Record<string, string[]>>({});
  const [topicosDescMap, setTopicosDescMap] = useState<Record<string, string>>({});
  const [agentesOuvidoria, setAgentesOuvidoria] = useState<{ id: string; nome: string; fotoPerfil?: { id: string; arquivo: string } }[]>([]);
  const [solicitacoesComAgente, setSolicitacoesComAgente] = useState<SolicitacaoFull[]>([]);
  const [loadingGraphs, setLoadingGraphs] = useState(false);

  const buildTimeQueryParams = useCallback((): Record<string, string | number> => {
    const queryParams: Record<string, string | number> = {};
    if (periodo === "Anual") {
      if (!appliedAno) return queryParams;
      const start = new Date(appliedAno, 0, 1, 0, 0, 0, 0);
      const end = new Date(appliedAno, 11, 31, 23, 59, 59, 999);
      queryParams.DataInicio = start.toISOString();
      queryParams.DataFim = end.toISOString();
    } else if (periodo === "AllTime") {
      const start = new Date(1900, 0, 1, 0, 0, 0, 0);
      const end = new Date();
      end.setHours(23, 59, 59, 999);
      queryParams.DataInicio = start.toISOString();
      queryParams.DataFim = end.toISOString();
    } else if (periodo === "custom" && appliedDateRange.from && appliedDateRange.to) {
      queryParams.DataInicio = appliedDateRange.from.toISOString();
      queryParams.DataFim = appliedDateRange.to.toISOString();
    } else if (periodo !== "custom") {
      const days = periodo === "7d" ? 7 : periodo === "30d" ? 30 : 90;
      queryParams.UltimosDias = days;
    }
    return queryParams;
  }, [periodo, appliedAno, appliedDateRange]);

  const buscarSolicitacoesPorBairro = useCallback(async (bairroNome: string): Promise<number> => {
    if (!AppConfig.municipioId || !AppConfig.ouvidoriaId) {
      const fetchAgenteAtual = useAgenteAtualStore.getState().fetchAgenteAtual;
      await fetchAgenteAtual();
    }
    if (!AppConfig.municipioId || !AppConfig.ouvidoriaId) {
      throw new Error("Configuração de município/ouvidoria não encontrada");
    }

    const timeParams = buildTimeQueryParams();

    const result = await api.get<unknown[]>({
      endpoint: `v1/municipios/${AppConfig.municipioId}/ouvidorias/${AppConfig.ouvidoriaId}/solicitacoes`,
      headers: { Accept: "application/vnd.data.minimal.v1+json" },
      queryParams: {
        Endereco: bairroNome,
        ...timeParams,
      },
      forceRefresh: true,
      serialize: (raw: unknown) => {
        const data = raw as Record<string, unknown>;
        if (Array.isArray(raw)) return raw as unknown[];
        if (data?.items && Array.isArray(data.items)) return data.items as unknown[];
        if (data?.data && Array.isArray(data.data)) return data.data as unknown[];
        return [];
      },
    });

    if ("failure" in result) {
      throw new Error("Erro ao buscar solicitações");
    }

    const allItems = Array.isArray(result.success.data) ? result.success.data : [];

    // A API já filtra pelo parâmetro Endereco; usar contagem direta
    return allItems.length;
  }, [buildTimeQueryParams]);

  const handleAddBairro = useCallback(async () => {
    const nome = search.trim();
    if (!nome) return;
    if (bairros.some((b) => b.name.toLowerCase() === nome.toLowerCase())) {
      setError("Bairro já adicionado");
      return;
    }
    if (bairros.length >= 12) {
      setError("Limite de 12 bairros atingido");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const count = await buscarSolicitacoesPorBairro(nome);
      setBairros((prev) => [
        ...prev,
        {
          name: nome,
          value: count,
          color: PIE_COLORS[prev.length % PIE_COLORS.length],
        },
      ]);
      setSearch("");
    } catch {
      setError("Erro ao buscar solicitações para este bairro");
    } finally {
      setLoading(false);
    }
  }, [search, bairros, buscarSolicitacoesPorBairro]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleAddBairro();
      }
    },
    [handleAddBairro]
  );

  const removeBairro = useCallback((nome: string) => {
    setBairros((prev) => prev.filter((b) => b.name !== nome));
    setError(null);
  }, []);

  const buscarTodasSolicitacoes = useCallback(async () => {
    if (!AppConfig.municipioId || !AppConfig.ouvidoriaId) {
      const fetchAgenteAtual = useAgenteAtualStore.getState().fetchAgenteAtual;
      await fetchAgenteAtual();
    }
    if (!AppConfig.municipioId || !AppConfig.ouvidoriaId) return;

    setLoadingSolicitacoes(true);
    const timeParams = buildTimeQueryParams();

    try {
      const result = await api.get<SolicitacaoFull[]>({
        endpoint: `v1/municipios/${AppConfig.municipioId}/ouvidorias/${AppConfig.ouvidoriaId}/solicitacoes`,
        headers: { Accept: "application/vnd.data.minimal.v1+json" },
        queryParams: timeParams,
        forceRefresh: true,
        serialize: (raw: unknown) => {
          const data = raw as Record<string, unknown>;
          if (Array.isArray(raw)) return raw as SolicitacaoFull[];
          if (data?.items && Array.isArray(data.items)) return data.items as SolicitacaoFull[];
          if (data?.data && Array.isArray(data.data)) return data.data as SolicitacaoFull[];
          return [];
        },
      });

      const allItems = !("failure" in result)
        ? Array.isArray(result.success.data) ? result.success.data : []
        : [];
      setSolicitacoesFull(allItems);
    } catch {
      setSolicitacoesFull([]);
    } finally {
      setLoadingSolicitacoes(false);
    }
  }, [buildTimeQueryParams]);

  // Busca solicitações leves (grafics.v1) para categorias e rankings
  const fetchData = useCallback(async () => {
    if (!AppConfig.municipioId || !AppConfig.ouvidoriaId) {
      const fetchAgenteAtual = useAgenteAtualStore.getState().fetchAgenteAtual;
      await fetchAgenteAtual();
    }
    if (!AppConfig.municipioId || !AppConfig.ouvidoriaId) return;

    setLoadingGraphs(true);
    try {
      const queryParams: Record<string, string | number> = {};
      if (periodo === "Anual") {
        if (!appliedAno) return;
        const start = new Date(appliedAno, 0, 1, 0, 0, 0, 0);
        const end = new Date(appliedAno, 11, 31, 23, 59, 59, 999);
        queryParams.DataInicio = start.toISOString();
        queryParams.DataFim = end.toISOString();
      } else if (periodo === "AllTime") {
        const start = new Date(1900, 0, 1, 0, 0, 0, 0);
        const end = new Date();
        end.setHours(23, 59, 59, 999);
        queryParams.DataInicio = start.toISOString();
        queryParams.DataFim = end.toISOString();
      } else if (periodo === "custom" && appliedDateRange.from && appliedDateRange.to) {
        queryParams.DataInicio = appliedDateRange.from.toISOString();
        queryParams.DataFim = appliedDateRange.to.toISOString();
      } else if (periodo !== "24h" && periodo !== "custom") {
        const days = periodo === "7d" ? 7 : periodo === "30d" ? 30 : 90;
        queryParams.UltimosDias = days;
      }

      const solResult = await api.get<Solicitacao[]>({
        endpoint: `v1/municipios/${AppConfig.municipioId}/ouvidorias/${AppConfig.ouvidoriaId}/solicitacoes`,
        headers: { Accept: "application/vnd.data.grafics.v1+json" },
        queryParams,
        forceRefresh: true,
        serialize: (raw: unknown) => {
          const data = raw as Record<string, unknown>;
          if (Array.isArray(raw)) return raw as Solicitacao[];
          if (data?.items && Array.isArray(data.items)) return data.items as Solicitacao[];
          if (data?.data && Array.isArray(data.data)) return data.data as Solicitacao[];
          return [];
        },
      });

      let solicitacoesList: Solicitacao[] = [];
      if (!("failure" in solResult)) {
        const resultData = solResult.success.data;
        solicitacoesList = Array.isArray(resultData) ? resultData : [];
      }
      setSolicitacoes(solicitacoesList);

      const topResult = await api.get<Topico[]>({
        endpoint: `v1/topicos`,
        headers: { Accept: "application/vnd.data.full.v1+json" },
        queryParams: { municipioId: AppConfig.municipioLong },
        forceRefresh: true,
        serialize: (raw: unknown) => {
          const data = raw as Record<string, unknown>;
          if (Array.isArray(raw)) return raw as Topico[];
          if (data?.items && Array.isArray(data.items)) return data.items as Topico[];
          if (data?.data && Array.isArray(data.data)) return data.data as Topico[];
          return [];
        },
      });

      if (!("failure" in topResult)) {
        const topicos = Array.isArray(topResult.success.data) ? topResult.success.data : [];
        const map: Record<string, string[]> = {};
        const descMap: Record<string, string> = {};
        for (const t of topicos) {
          if (t.id) {
            if (t.etiquetas) map[t.id] = t.etiquetas;
            if (t.descricao) descMap[t.id] = t.descricao;
          }
        }
        setTopicosMap(map);
        setTopicosDescMap(descMap);
      }
    } catch {
      setSolicitacoes([]);
    } finally {
      setLoadingGraphs(false);
    }
  }, [periodo, appliedAno, appliedDateRange]);

  // Busca agentes da ouvidoria para nomes/fotos
  const fetchAgentesOuvidoria = useCallback(async () => {
    try {
      if (!AppConfig.ouvidoriaId) return;
      const result = await api.get<Record<string, unknown>[]>({
        endpoint: `v1/ouvidorias/${AppConfig.ouvidoriaId}/agentes`,
        requiredToken: true,
        headers: { accept: "application/vnd.data.compact.v1+json" },
        forceRefresh: true,
        serialize: (raw) => {
          if (Array.isArray(raw)) return raw as Record<string, unknown>[];
          const data = raw as Record<string, unknown>;
          if (data?.items && Array.isArray(data.items)) return data.items as Record<string, unknown>[];
          if (data?.data && Array.isArray(data.data)) return data.data as Record<string, unknown>[];
          return [];
        },
      });
      if (!("failure" in result)) {
        const items = Array.isArray(result.success.data) ? result.success.data : [];
        const mapped = items.map((a: Record<string, unknown>) => {
          const fp = a.fotoPerfil as Record<string, unknown> | undefined;
          return {
            id: String(a.id ?? ""),
            nome: String(a.nome || a.name || "Sem nome"),
            fotoPerfil: fp ? { id: String(fp.id ?? ""), arquivo: String(fp.arquivo ?? "") } : undefined,
          };
        });
        setAgentesOuvidoria(mapped);
      } else {
        setAgentesOuvidoria([]);
      }
    } catch {
      setAgentesOuvidoria([]);
    }
  }, []);

  // Resolve nome do agente: prioriza lookup por ID no cache de agentes da ouvidoria
  const resolveAgente = useCallback(
    (s: SolicitacaoFull): { nome: string; foto?: string } => {
      const id = s.agenteResponsavel?.id;
      if (id && agentesOuvidoria.length > 0) {
        const encontrado = agentesOuvidoria.find((a) => a.id === id);
        if (encontrado) {
          return {
            nome: encontrado.nome || s.agenteResponsavel?.nome || "Sem nome",
            foto: encontrado.fotoPerfil?.arquivo || (s.agenteResponsavel?.fotoPerfil?.arquivo as string | undefined),
          };
        }
      }
      if (s.agenteResponsavel?.nome) {
        return {
          nome: s.agenteResponsavel.nome,
          foto: s.agenteResponsavel.fotoPerfil?.arquivo as string | undefined,
        };
      }
      return { nome: "Não atribuído", foto: undefined };
    },
    [agentesOuvidoria],
  );

  // Busca detalhes (incluindo agenteResponsavel) das solicitações resolvidas
  const fetchAgentesParaSolicitacoes = useCallback(
    async (lista: SolicitacaoFull[]) => {
      const resolvidas = lista.filter((s) => normalize(s.status) === normalize("Resolvida"));
      if (resolvidas.length === 0) {
        setSolicitacoesComAgente([]);
        return;
      }

      const detalhes: SolicitacaoFull[] = [];
      const batchSize = 5;

      for (let i = 0; i < resolvidas.length; i += batchSize) {
        const batch = resolvidas.slice(i, i + batchSize);
        const promises = batch.map((s) =>
          api.get<SolicitacaoFull>({
            endpoint: `v1/solicitacoes/${s.id}`,
            headers: { Accept: "application/vnd.data.grafics.v1+json" },
            forceRefresh: true,
            serialize: (raw) => {
              if (typeof raw === "object" && raw !== null) return raw as SolicitacaoFull;
              return undefined as unknown as SolicitacaoFull;
            },
          }),
        );

        const results = await Promise.all(promises);
        for (const result of results) {
          if (!("failure" in result) && result.success.data) {
            detalhes.push(result.success.data);
          }
        }
      }

      setSolicitacoesComAgente(detalhes);
    },
    [],
  );

  useEffect(() => {
    buscarTodasSolicitacoes();
    fetchData();
    fetchAgentesOuvidoria();
  }, [buscarTodasSolicitacoes, fetchData, fetchAgentesOuvidoria]);

  useEffect(() => {
    if (solicitacoesFull.length > 0) {
      fetchAgentesParaSolicitacoes(solicitacoesFull);
    }
  }, [solicitacoesFull, fetchAgentesParaSolicitacoes]);

  // Em aberto por faixa de dias desde abertura
  const abertoPorDiasData = useMemo(() => {
    const emAberto = solicitacoesFull.filter((s) => {
      const st = normalize(s.status);
      return st !== normalize("Resolvida") && st !== normalize("Reprovada");
    });
    const hoje = new Date();
    const faixas = [
      { name: "1-5 dias", min: 0, max: 5, value: 0 },
      { name: "6-10 dias", min: 6, max: 10, value: 0 },
      { name: "11-15 dias", min: 11, max: 15, value: 0 },
      { name: "16-20 dias", min: 16, max: 20, value: 0 },
      { name: "21-25 dias", min: 21, max: 25, value: 0 },
      { name: "26-31 dias", min: 26, max: 31, value: 0 },
      { name: "31+ dias", min: 32, max: Infinity, value: 0 },
    ];
    for (const s of emAberto) {
      const dias = Math.floor((hoje.getTime() - new Date(s.dataCriacao).getTime()) / (1000 * 60 * 60 * 24));
      const faixa = faixas.find((f) => dias >= f.min && dias <= f.max);
      if (faixa) faixa.value++;
    }
    return faixas.filter((f) => f.value > 0);
  }, [solicitacoesFull]);

  const days =
    periodo === "24h" ? 1 : periodo === "7d" ? 7 : periodo === "30d" ? 30 : 90;

  const filtered = useMemo(() => {
    if (periodo === "custom" && appliedDateRange.from && appliedDateRange.to) {
      const from = new Date(appliedDateRange.from);
      from.setHours(0, 0, 0, 0);
      const to = new Date(appliedDateRange.to);
      to.setHours(23, 59, 59, 999);
      return solicitacoes.filter((s) => {
        const creationDate = new Date(s.dataCriacao);
        return creationDate >= from && creationDate <= to;
      });
    }
    if (periodo === "Anual") {
      if (!appliedAno) return [];
      const start = new Date(appliedAno, 0, 1, 0, 0, 0, 0);
      const end = new Date(appliedAno, 11, 31, 23, 59, 59, 999);
      return solicitacoes.filter((s) => {
        const creationDate = new Date(s.dataCriacao);
        return creationDate >= start && creationDate <= end;
      });
    }
    if (periodo === "AllTime") {
      return solicitacoes;
    }
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);
    return solicitacoes.filter((s) => new Date(s.dataCriacao) >= cutoff);
  }, [solicitacoes, days, periodo, appliedDateRange, appliedAno]);

  // Por categoria: etiquetas do tópico (primeira etiqueta de cada solicitação)
  const categoryData = useMemo(() => {
    const etiquetas = filtered.map((s) => {
      const topicoId = s.topico?.id;
      // Busca a primeira etiqueta do tópico no mapa
      if (topicoId && topicosMap[topicoId]?.length > 0) {
        return topicosMap[topicoId][0];
      }
      return "";
    });
    return aggregate(etiquetas).slice(0, 8);
  }, [filtered, topicosMap]);

  // Taxa de Resolução (donut chart data) — apenas por status, sem estadoAprovacao
  const taxaResolucaoData = useMemo(() => {
    const res = solicitacoesFull.filter((s) => normalize(s.status) === normalize("resolvida")).length;
    const pend = solicitacoesFull.filter((s) => normalize(s.status) === normalize("pendente")).length;
    const analise = solicitacoesFull.filter((s) => {
      const st = normalize(s.status);
      return st === normalize("EmAnalise") || st === normalize("em analise") || st === "emanalise";
    }).length;
    const agend = solicitacoesFull.filter((s) => normalize(s.status) === normalize("agendada")).length;
    return [
      { name: "Resolvidas", value: res, color: "hsl(var(--success))", icon: CheckCircle2 },
      { name: "Pendentes", value: pend, color: "hsl(var(--destructive))", icon: Circle },
      { name: "Em Análise", value: analise, color: "hsl(25, 95%, 53%)", icon: Timer },
      { name: "Agendadas", value: agend, color: "#CEDC38", icon: CalendarCheck },
    ];
  }, [solicitacoesFull]);

  // Estado de Aprovação (donut chart)
  const estadoAprovacaoData = useMemo(() => {
    const aprov = solicitacoesFull.filter((s) => normalize(s.estadoAprovacao) === normalize("Aprovada")).length;
    const aguard = solicitacoesFull.filter((s) => normalize(s.estadoAprovacao) === normalize("Aguardando Aprovação")).length;
    const reprov = solicitacoesFull.filter((s) => normalize(s.estadoAprovacao) === normalize("Reprovada")).length;
    return [
      { name: "Aprovadas", value: aprov, color: "hsl(var(--success))", icon: CheckCircle2 },
      { name: "Aguard. Aprovação", value: aguard, color: "hsl(var(--warning))", icon: ShieldAlert },
      { name: "Reprovadas", value: reprov, color: "hsl(var(--destructive))", icon: Ban },
    ].filter((d) => d.value > 0);
  }, [solicitacoesFull]);

  // Top 5 Tópicos com mais demandas
  const topTopicosData = useMemo(() => {
    return aggregate(
      solicitacoes.map((s) => {
        if (s.topico?.descricao) return s.topico.descricao;
        if (s.topico?.id && topicosDescMap[s.topico.id]) return topicosDescMap[s.topico.id];
        return "Sem título";
      }),
    ).slice(0, 5);
  }, [solicitacoes, topicosDescMap]);

  // Top 5 Agentes da ouvidoria com mais demandas resolvidas
  const topAgentesInfo = useMemo(() => {
    const resolvidas = solicitacoesComAgente.filter((s) => normalize(s.status) === normalize("Resolvida"));
    const contagens = aggregate(resolvidas.map((s) => resolveAgente(s).nome));
    return contagens.slice(0, 5).map((a) => {
      const agente = agentesOuvidoria.find((ag) => ag.nome === a.name);
      return { nome: a.name, foto: agente?.fotoPerfil?.arquivo };
    });
  }, [solicitacoesComAgente, agentesOuvidoria]);

  // Evolução de resolvidas por agente ao longo do tempo
  const evolucaoAgentesData = useMemo(() => {
    if (solicitacoesComAgente.length === 0 || topAgentesInfo.length === 0) return [];
    const resolvidas = solicitacoesComAgente.filter((s) => normalize(s.status) === normalize("Resolvida"));
    const isLongo = periodo === "AllTime" || periodo === "Anual" || periodo === "90d";
    const topNomes = topAgentesInfo.map((a) => a.nome);

    if (isLongo) {
      const meses = new Map<string, Record<string, number>>();
      for (const s of resolvidas) {
        const d = new Date(s.dataConclusao || s.dataCriacao);
        const key = `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getFullYear()).slice(-2)}`;
        const nome = resolveAgente(s).nome;
        if (!topNomes.includes(nome)) continue;
        if (!meses.has(key)) meses.set(key, {});
        const m = meses.get(key)!;
        m[nome] = (m[nome] || 0) + 1;
      }
      return Array.from(meses.entries()).map(([name, counts]) => ({
        name,
        ...topNomes.reduce((acc, nome) => ({ ...acc, [nome]: counts[nome] || 0 }), {} as Record<string, number>),
      }));
    }

    const hoje = new Date();
    hoje.setHours(12, 0, 0, 0);
    const diasCount =
      periodo === "24h" ? 1 :
      periodo === "7d" ? 7 :
      periodo === "30d" ? 30 :
      periodo === "custom" && appliedDateRange.from && appliedDateRange.to
        ? Math.ceil((appliedDateRange.to.getTime() - appliedDateRange.from.getTime()) / (1000 * 60 * 60 * 24)) + 1
        : 30;
    const refDate = periodo === "custom" && appliedDateRange.to ? appliedDateRange.to : hoje;
    const pontos: { name: string }[] & Record<string, number>[] = [];

    for (let i = diasCount - 1; i >= 0; i--) {
      const d = new Date(refDate);
      d.setDate(d.getDate() - i);
      const dayStr = toLocalDateKey(d);
      const label = d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
      const counts: Record<string, number> = {};
      for (const s of resolvidas) {
        if (toLocalDateKey(new Date(s.dataConclusao || s.dataCriacao)) === dayStr) {
          const nome = resolveAgente(s).nome;
          if (topNomes.includes(nome)) counts[nome] = (counts[nome] || 0) + 1;
        }
      }
      pontos.push({
        name: label,
        ...topNomes.reduce((acc, nome) => ({ ...acc, [nome]: counts[nome] || 0 }), {} as Record<string, number>),
      } as { name: string } & Record<string, number>);
    }
    return pontos;
  }, [solicitacoesComAgente, periodo, topAgentesInfo, appliedDateRange, agentesOuvidoria]);

  // Total de demandas em aberto
  const totalEmAberto = useMemo(() => {
    return solicitacoesFull.filter((s) => {
      const st = normalize(s.status);
      return st !== normalize("Resolvida") && st !== normalize("Reprovada");
    }).length;
  }, [solicitacoesFull]);

  // Evolução temporal: novas vs resolvidas
  const evolucaoData = useMemo(() => {
    if (solicitacoesFull.length === 0) return [];

    const hoje = new Date();
    hoje.setHours(12, 0, 0, 0);
    const pontos: { name: string; novas: number; resolvidas: number }[] = [];

    // --- PERÍODOS LONGOS: agrupar por mês ---
    if (periodo === "AllTime" || periodo === "Anual") {
      const anoRef = periodo === "Anual" ? appliedAno : null;

      const datas = solicitacoesFull
        .map((s) => new Date(s.dataCriacao))
        .filter((d) => !isNaN(d.getTime()))
        .sort((a, b) => a.getTime() - b.getTime());

      if (datas.length === 0) return [];

      const primeiro = datas[0];
      const ultimo = hoje;

      const meses: { key: string; label: string; ano: number; mes: number }[] = [];
      const cursor = new Date(primeiro.getFullYear(), primeiro.getMonth(), 1);
      const fim = new Date(ultimo.getFullYear(), ultimo.getMonth(), 1);

      while (cursor <= fim) {
        const ano = cursor.getFullYear();
        const mes = cursor.getMonth();
        if (!anoRef || ano === anoRef) {
          const label = anoRef
            ? cursor.toLocaleDateString("pt-BR", { month: "short" }).replace(".", "")
            : `${String(mes + 1).padStart(2, "0")}/${String(ano).slice(-2)}`;
          meses.push({ key: `${ano}-${String(mes + 1).padStart(2, "0")}`, label, ano, mes });
        }
        cursor.setMonth(cursor.getMonth() + 1);
      }

      for (const m of meses) {
        const novas = solicitacoesFull.filter((s) => {
          const d = new Date(s.dataCriacao);
          return d.getFullYear() === m.ano && d.getMonth() === m.mes;
        }).length;
        const resolvidas = solicitacoesFull.filter((s) => {
          if (normalize(s.status) !== normalize("Resolvida")) return false;
          const d = new Date(s.dataConclusao || s.dataCriacao);
          return d.getFullYear() === m.ano && d.getMonth() === m.mes;
        }).length;
        pontos.push({ name: m.label, novas, resolvidas });
      }

      return pontos;
    }

    // --- PERÍODOS CURTOS: agrupar por dia ---
    const diasCount =
      periodo === "24h" ? 1 :
      periodo === "7d" ? 7 :
      periodo === "30d" ? 30 :
      periodo === "90d" ? 90 :
      periodo === "custom" && appliedDateRange.from && appliedDateRange.to
        ? Math.ceil((appliedDateRange.to.getTime() - appliedDateRange.from.getTime()) / (1000 * 60 * 60 * 24)) + 1
        : 30;

    const refDate = periodo === "custom" && appliedDateRange.to ? appliedDateRange.to : hoje;

    for (let i = diasCount - 1; i >= 0; i--) {
      const d = new Date(refDate);
      d.setDate(d.getDate() - i);
      const dayStr = toLocalDateKey(d);
      const label = d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
      const novas = solicitacoesFull.filter((s) => toLocalDateKey(new Date(s.dataCriacao)) === dayStr).length;
      const resolvidas = solicitacoesFull.filter((s) => {
        if (normalize(s.status) !== normalize("Resolvida")) return false;
        return toLocalDateKey(new Date(s.dataConclusao || s.dataCriacao)) === dayStr;
      }).length;
      pontos.push({ name: label, novas, resolvidas });
    }

    return pontos;
  }, [solicitacoesFull, periodo, appliedAno, appliedDateRange]);

  // Re-buscar todos os bairros quando o período mudar
  useEffect(() => {
    if (bairros.length === 0) return;

    let cancelled = false;
    const refreshAll = async () => {
      setLoading(true);
      try {
        const updated = await Promise.all(
          bairros.map(async (b) => {
            const count = await buscarSolicitacoesPorBairro(b.name);
            return { ...b, value: count };
          })
        );
        if (!cancelled) {
          setBairros(updated);
        }
      } catch {
        // ignore
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    refreshAll();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodo, appliedAno, appliedDateRange]);

  const totalSolicitacoes = useMemo(
    () => bairros.reduce((sum, b) => sum + b.value, 0),
    [bairros]
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="heading-section text-foreground flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-primary" />
            Gráficos por Região
          </h2>
          <p className="text-caption mt-1">
            Pesquise bairros para visualizar a distribuição de solicitações
          </p>
        </div>
        <Select value={periodo} onValueChange={(value) => {
          if (value === "custom") {
            setShowDateModal(true);
          } else if (value === "Anual") {
            setPeriodo("Anual");
            setAppliedAno(undefined);
            setAno(appliedAno ?? new Date().getFullYear());
            setShowYearModal(true);
          } else {
            setPeriodo(value);
            setAppliedAno(undefined);
            setAno(undefined);
          }
        }}>
          <SelectTrigger className="w-auto min-w-[140px] max-w-[300px]">
            <Calendar className="h-4 w-4 mr-2 flex-shrink-0" />
            <SelectValue placeholder="Período">
              {periodo === "custom" && appliedDateRange.from && appliedDateRange.to ? (
                <span className="text-sm truncate">
                  de {appliedDateRange.from.toLocaleDateString("pt-BR")} até {appliedDateRange.to.toLocaleDateString("pt-BR")}
                </span>
              ) : periodo === "Anual" && appliedAno ? (
                <span className="text-sm truncate">Ano: {appliedAno}</span>
              ) : (
                periodo === "7d" ? "7 dias" :
                periodo === "30d" ? "30 dias" :
                periodo === "90d" ? "90 dias" :
                periodo === "AllTime" ? "Todo o tempo" :
                periodo === "Anual" ? "Anual" : "Personalizado"
              )}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7d">7 dias</SelectItem>
            <SelectItem value="30d">30 dias</SelectItem>
            <SelectItem value="90d">90 dias</SelectItem>
            <SelectItem value="AllTime">Todo o tempo</SelectItem>
            <SelectItem value="Anual">Anual</SelectItem>
            <SelectItem value="custom">Personalizado</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Search */}
      <div className="surface-elevated p-4 space-y-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Digite o nome do bairro e pressione Enter..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                if (error) setError(null);
              }}
              onKeyDown={handleKeyDown}
              className="pl-10"
              disabled={loading}
            />
          </div>
          <Button
            onClick={handleAddBairro}
            disabled={loading || !search.trim()}
            className="min-w-[120px]"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <BarChart3 className="h-4 w-4 mr-2" />
            )}
            Adicionar
          </Button>
        </div>

        {error && (
          <p className="text-sm text-destructive">{error}</p>
        )}

        {/* Chips */}
        {bairros.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {bairros.map((bairro) => (
              <div
                key={bairro.name}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-surface-container border border-border"
              >
                <span
                  className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: bairro.color }}
                />
                <span>{bairro.name}</span>
                <span className="text-muted-foreground text-xs">({bairro.value})</span>
                <button
                  onClick={() => removeBairro(bairro.name)}
                  className="ml-1 text-muted-foreground hover:text-destructive transition-colors"
                  aria-label={`Remover ${bairro.name}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Chart */}
      {bairros.length > 0 ? (
        <div className="surface-elevated p-4">
          <div className="min-h-[300px] flex flex-col lg:flex-row items-start gap-6">
            <div className="w-full lg:w-[280px] h-[280px] flex-shrink-0 relative mx-auto lg:mx-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={bairros}
                    cx="50%"
                    cy="50%"
                    innerRadius={80}
                    outerRadius={120}
                    paddingAngle={2}
                    dataKey="value"
                    stroke="none"
                  >
                    {bairros.map((_entry, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(0,0%,100%)",
                      border: "1px solid hsl(200,15%,88%)",
                      borderRadius: "8px",
                      fontSize: "12px",
                    }}
                    formatter={(value: number, name: string) => {
                      const pct =
                        totalSolicitacoes > 0
                          ? Math.round((value / totalSolicitacoes) * 100)
                          : 0;
                      return [`${value} (${pct}%)`, name];
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              {/* Centro do donut */}
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-2xl font-bold text-foreground">
                  {totalSolicitacoes}
                </span>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  Total
                </span>
              </div>
            </div>

            {/* Legenda com mini-cards */}
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 content-start w-full">
              {bairros.map((item, i) => {
                const pct =
                  totalSolicitacoes > 0
                    ? Math.round((item.value / totalSolicitacoes) * 100)
                    : 0;
                const color = PIE_COLORS[i % PIE_COLORS.length];
                return (
                  <div
                    key={item.name}
                    className="flex items-center gap-3 p-3 rounded-lg border border-border/40 hover:bg-surface-container/40 transition-colors"
                  >
                    <div
                      className="h-8 w-8 rounded-md flex items-center justify-center flex-shrink-0"
                      style={{
                        backgroundColor: color
                          .replace("hsl(", "hsla(")
                          .replace(")", " / 0.1)"),
                        color,
                      }}
                    >
                      <MapPin className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-muted-foreground truncate">
                        {item.name}
                      </p>
                      <div className="flex items-baseline gap-1">
                        <span className="text-sm font-bold">{item.value}</span>
                        <span className="text-xs font-medium text-muted-foreground">
                          {pct}%
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => removeBairro(item.name)}
                      className="text-muted-foreground hover:text-destructive transition-colors flex-shrink-0"
                      aria-label={`Remover ${item.name}`}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        <div className="surface-elevated p-8 flex flex-col items-center justify-center text-center gap-3">
          <MapPin className="h-10 w-10 text-muted-foreground/40" />
          <div>
            <p className="text-sm font-medium text-foreground">
              Nenhum bairro adicionado
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Pesquise pelo nome de um bairro acima para começar
            </p>
          </div>
        </div>
      )}

      {/* Tempo em Aberto */}
      <section className="space-y-3">
        <div>
          <h3 className="heading-subsection text-foreground">Tempo em Aberto</h3>
          <p className="text-caption mt-0.5">Distribuição de demandas em aberto por dias desde a abertura</p>
        </div>
        <div className="surface-elevated p-6">
          <div className="h-[200px]">
            {loadingSolicitacoes ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : abertoPorDiasData.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <span className="text-muted-foreground text-sm">Nenhuma demanda em aberto</span>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={abertoPorDiasData} layout="vertical" margin={{ top: 10, right: 20, left: 20, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted-foreground)/0.15)" horizontal={true} vertical={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} allowDecimals={false} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 12, fill: "hsl(var(--foreground))", fontWeight: 500 }} tickLine={false} axisLine={false} width={90} />
                  <Tooltip
                    cursor={{ fill: "transparent" }}
                    contentStyle={{
                      backgroundColor: "hsl(var(--popover))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                      fontSize: "12px",
                      color: "hsl(var(--popover-foreground))",
                    }}
                    formatter={(value: number) => [`${value} demandas`, ""]}
                    separator=""
                  />
                  <Bar dataKey="value" fill="hsl(var(--info))" radius={[0, 4, 4, 0]} barSize={24} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </section>

      {/* Gráficos do Dashboard */}
      {(loadingSolicitacoes || loadingGraphs) && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-sm text-muted-foreground">Carregando estatísticas...</span>
        </div>
      )}

      {/* Por Categoria (tópico) — apenas ouvidoria geral */}
      {isGeralOuvidoria && (
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="surface-elevated p-5 animate-fade-in lg:col-span-3">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="heading-subsection text-foreground">Por Categoria</h3>
                <p className="text-caption mt-0.5">Tópico da solicitação</p>
              </div>
            </div>
            <div className="h-[220px] flex items-center justify-center">
              {categoryData.length === 0 ? (
                <span className="text-muted-foreground text-sm">Sem dados</span>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={categoryData}
                      cx="50%"
                      cy="50%"
                      innerRadius={45}
                      outerRadius={75}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {categoryData.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(0,0%,100%)",
                        border: "1px solid hsl(200,15%,88%)",
                        borderRadius: "8px",
                        fontSize: "12px",
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="grid grid-cols-1 gap-1 mt-2">
              {categoryData.slice(0, 5).map((cat, i) => (
                <div key={cat.name} className="flex items-center gap-2">
                  <span
                    className="h-2 w-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }}
                  />
                  <span className="text-xs text-muted-foreground truncate flex-1">
                    {cat.name}
                  </span>
                  <span className="text-xs font-medium">{cat.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Status do Atendimento + Estado de Aprovação */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="heading-subsection text-foreground">Status do Atendimento</h3>
            <p className="text-caption mt-0.5">Taxa de resolução e demandas em aberto no período</p>
          </div>
        </div>
        <div className="grid gap-4 lg:grid-cols-3">
          {/* Donut Chart */}
          <div className="surface-elevated p-4 lg:col-span-2">
            <div className="min-h-[180px] flex items-start gap-5">
              <div className="w-[150px] h-[150px] flex-shrink-0 relative">
                {taxaResolucaoData.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <span className="text-muted-foreground text-sm">Sem dados</span>
                  </div>
                ) : (
                  <>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={taxaResolucaoData}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={70}
                          paddingAngle={2}
                          dataKey="value"
                          stroke="none"
                        >
                          {taxaResolucaoData.map((entry, i) => (
                            <Cell key={i} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "hsl(0,0%,100%)",
                            border: "1px solid hsl(200,15%,88%)",
                            borderRadius: "8px",
                            fontSize: "12px",
                          }}
                          formatter={(value: number, name: string) => {
                            const totalVal = taxaResolucaoData.reduce((a, b) => a + b.value, 0);
                            const pct = totalVal > 0 ? Math.round((value / totalVal) * 100) : 0;
                            return [`${value} (${pct}%)`, name];
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    {/* Centro do donut */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                      <span className="text-lg font-bold text-foreground">
                        {taxaResolucaoData.reduce((a, b) => a + b.value, 0)}
                      </span>
                      <span className="text-[9px] uppercase tracking-wider text-muted-foreground">Total</span>
                    </div>
                  </>
                )}
              </div>
              {/* Legenda com mini-cards */}
              <div className="flex-1 grid grid-cols-2 gap-2 content-start">
                {taxaResolucaoData.map((item) => {
                  const totalVal = taxaResolucaoData.reduce((a, b) => a + b.value, 0);
                  const pct = totalVal > 0 ? Math.round((item.value / totalVal) * 100) : 0;
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.name}
                      className="flex items-center gap-2 p-2 rounded-md border border-border/40 hover:bg-surface-container/40 transition-colors"
                    >
                      <div
                        className="h-7 w-7 rounded-md flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: item.color.startsWith("#") ? `${item.color}1A` : item.color.replace("hsl(", "hsla(").replace(")", " / 0.1)"), color: item.color }}
                      >
                        <Icon className="h-3.5 w-3.5" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[11px] text-muted-foreground truncate">{item.name}</p>
                        <div className="flex items-baseline gap-1">
                          <span className="text-xs font-bold">{item.value}</span>
                          <span className="text-[10px] font-medium text-muted-foreground">{pct}%</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Card de Demandas em Aberto */}
          <div className="surface-elevated p-6 flex flex-col">
            <div className="mb-5">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Demandas em Aberto</p>
              <p className="text-5xl font-bold text-foreground mt-1">{totalEmAberto}</p>
              <p className="text-xs text-muted-foreground mt-1">requerem atenção no período</p>
            </div>
            <div className="flex-1 space-y-2.5">
              {taxaResolucaoData
                .filter((d) => d.name !== "Resolvidas" && d.name !== "Reprovadas")
                .map((item) => {
                  const totalAberto = taxaResolucaoData
                    .filter((d) => d.name !== "Resolvidas" && d.name !== "Reprovadas")
                    .reduce((a, b) => a + b.value, 0);
                  const pct = totalAberto > 0 ? Math.round((item.value / totalAberto) * 100) : 0;
                  const Icon = item.icon;
                  return (
                    <div key={item.name}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <Icon className="h-3.5 w-3.5" style={{ color: item.color }} />
                          <span className="text-xs text-muted-foreground">{item.name}</span>
                        </div>
                        <span className="text-xs font-semibold">{item.value}</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{ width: `${pct}%`, backgroundColor: item.color }}
                        />
                      </div>
                    </div>
                  );
                })}
            </div>
            {/* Barra de progresso geral: resolvidas vs em aberto */}
            {taxaResolucaoData.length > 0 && (
              <div className="mt-5 pt-4 border-t border-border/40">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Taxa de resolução</p>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden flex">
                    {taxaResolucaoData
                      .filter((d) => d.name === "Resolvidas")
                      .map((d) => (
                        <div
                          key={d.name}
                          className="h-full"
                          style={{
                            width: `${taxaResolucaoData.reduce((a, b) => a + b.value, 0) > 0 ? Math.round((d.value / taxaResolucaoData.reduce((a, b) => a + b.value, 0)) * 100) : 0}%`,
                            backgroundColor: d.color,
                          }}
                        />
                      ))}
                  </div>
                  <span className="text-xs font-bold text-green-600">
                    {taxaResolucaoData.reduce((a, b) => a + b.value, 0) > 0
                      ? Math.round(
                          (taxaResolucaoData.find((d) => d.name === "Resolvidas")?.value || 0) /
                            taxaResolucaoData.reduce((a, b) => a + b.value, 0) *
                            100,
                        )
                      : 0}%
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Estado de Aprovação */}
      <div className="grid gap-4">
        <section className="space-y-3">
          <div>
            <h3 className="heading-subsection text-foreground">Estado de Aprovação</h3>
            <p className="text-caption mt-0.5">Distribuição das solicitações por estado de aprovação</p>
          </div>
          <div className="surface-elevated p-4">
            <div className="min-h-[160px] flex items-start gap-5">
              <div className="w-[150px] h-[150px] flex-shrink-0 relative">
                {estadoAprovacaoData.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <span className="text-muted-foreground text-sm">Sem dados</span>
                  </div>
                ) : (
                  <>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={estadoAprovacaoData}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={70}
                          paddingAngle={2}
                          dataKey="value"
                          stroke="none"
                        >
                          {estadoAprovacaoData.map((entry, i) => (
                            <Cell key={i} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "hsl(0,0%,100%)",
                            border: "1px solid hsl(200,15%,88%)",
                            borderRadius: "8px",
                            fontSize: "12px",
                          }}
                          formatter={(value: number, name: string) => {
                            const totalVal = estadoAprovacaoData.reduce((a, b) => a + b.value, 0);
                            const pct = totalVal > 0 ? Math.round((value / totalVal) * 100) : 0;
                            return [`${value} (${pct}%)`, name];
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    {/* Centro do donut */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                      <span className="text-lg font-bold text-foreground">
                        {estadoAprovacaoData.reduce((a, b) => a + b.value, 0)}
                      </span>
                      <span className="text-[9px] uppercase tracking-wider text-muted-foreground">Total</span>
                    </div>
                  </>
                )}
              </div>
              {/* Legenda com mini-cards */}
              <div className="flex-1 grid grid-cols-2 sm:grid-cols-3 gap-2 content-start">
                {estadoAprovacaoData.map((item) => {
                  const totalVal = estadoAprovacaoData.reduce((a, b) => a + b.value, 0);
                  const pct = totalVal > 0 ? Math.round((item.value / totalVal) * 100) : 0;
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.name}
                      className="flex items-center gap-2 p-2 rounded-md border border-border/40 hover:bg-surface-container/40 transition-colors"
                    >
                      <div
                        className="h-7 w-7 rounded-md flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: item.color.startsWith("#") ? `${item.color}1A` : item.color.replace("hsl(", "hsla(").replace(")", " / 0.1)"), color: item.color }}
                      >
                        <Icon className="h-3.5 w-3.5" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[11px] text-muted-foreground truncate">{item.name}</p>
                        <div className="flex items-baseline gap-1">
                          <span className="text-xs font-bold">{item.value}</span>
                          <span className="text-[10px] font-medium text-muted-foreground">{pct}%</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Top 5: Tópicos + Agentes */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="heading-subsection text-foreground">Rankings</h3>
            <p className="text-caption mt-0.5">Top 5 categorias e agentes com mais demandas resolvidas</p>
          </div>
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          {/* Top 5 Tópicos */}
          <div className="surface-elevated p-6">
            <h4 className="text-sm font-semibold mb-4 flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-primary flex-shrink-0" />
              Top 5 Categorias
            </h4>
            <div className="space-y-3">
              {topTopicosData.length === 0 ? (
                <span className="text-muted-foreground text-sm">Sem dados</span>
              ) : (
                topTopicosData.map((item, i) => {
                  const max = topTopicosData[0]?.value || 1;
                  const pct = Math.round((item.value / max) * 100);
                  return (
                    <div key={item.name}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm truncate flex-1 pr-2" title={item.name}>{item.name}</span>
                        <span className="text-sm font-semibold">{item.value}</span>
                      </div>
                      <div className="h-2 rounded-full bg-muted overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{
                            width: `${pct}%`,
                            backgroundColor: PIE_COLORS[i % PIE_COLORS.length],
                          }}
                        />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
          {/* Top 5 Agentes — Evolução Temporal de Resolvidas */}
          <div className="surface-elevated p-6">
            <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-primary flex-shrink-0" />
              Top 5 Agentes (Resolvidas)
            </h4>
            <p className="text-[11px] text-muted-foreground mb-3">
              {periodo === "AllTime" || periodo === "Anual" || periodo === "90d"
                ? "Evolução mensal de demandas resolvidas por agente"
                : "Evolução diária de demandas resolvidas por agente"}
            </p>
            <div className="h-[220px]">
              {evolucaoAgentesData.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <span className="text-muted-foreground text-sm">Sem dados</span>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={evolucaoAgentesData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted-foreground)/0.15)" vertical={false} />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                      tickLine={false}
                      axisLine={{ stroke: "hsl(var(--border))" }}
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                      tickLine={false}
                      axisLine={false}
                      allowDecimals={false}
                      width={25}
                    />
                    <Tooltip
                      cursor={{ fill: "transparent" }}
                      contentStyle={{
                        backgroundColor: "hsl(var(--popover))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px",
                        fontSize: "12px",
                        color: "hsl(var(--popover-foreground))",
                      }}
                    />
                    {topAgentesInfo.map((_a, i) => (
                      <Bar
                        key={_a.nome}
                        dataKey={_a.nome}
                        stackId="a"
                        fill="hsl(var(--success))"
                        radius={i === topAgentesInfo.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                        barSize={20}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
            {/* Mini-legenda dos agentes */}
            {topAgentesInfo.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1">
                {topAgentesInfo.map((a) => (
                  <div key={a.nome} className="flex items-center gap-1.5">
                    {a.foto ? (
                      <img src={a.foto} alt="" className="h-4 w-4 rounded-full object-cover flex-shrink-0" />
                    ) : (
                      <span className="h-4 w-4 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 text-[8px] font-bold text-green-700">
                        {a.nome.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                      </span>
                    )}
                    <span className="text-[10px] text-muted-foreground truncate max-w-[80px]" title={a.nome}>
                      {a.nome}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Evolução: Novas x Resolvidas */}
      <section className="space-y-3">
        <div>
          <h3 className="heading-subsection text-foreground">Evolução: Novas x Resolvidas</h3>
          <p className="text-caption mt-0.5">Comparativo de solicitações criadas e resolvidas ao longo do tempo</p>
        </div>
        <div className="surface-elevated p-6">
          <div className="h-[300px]">
            {evolucaoData.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <span className="text-muted-foreground text-sm">Sem dados</span>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={evolucaoData} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                  <defs>
                    <linearGradient id="gradNovas" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--info))" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="hsl(var(--info))" stopOpacity={0.01} />
                    </linearGradient>
                    <linearGradient id="gradResolvidas" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--success))" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="hsl(var(--success))" stopOpacity={0.01} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--muted-foreground)/0.15)" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={{ stroke: "hsl(var(--border))" }} />
                  <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} allowDecimals={false} width={30} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--popover))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                      fontSize: "12px",
                      color: "hsl(var(--popover-foreground))",
                    }}
                  />
                  <Area type="monotone" dataKey="novas" name="Novas" stroke="hsl(var(--info))" strokeWidth={2} fill="url(#gradNovas)" dot={false} activeDot={{ r: 4 }} />
                  <Area type="monotone" dataKey="resolvidas" name="Resolvidas" stroke="hsl(var(--success))" strokeWidth={2} fill="url(#gradResolvidas)" dot={false} activeDot={{ r: 4 }} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </section>

      {/* Modal de Seleção de Intervalo Personalizado */}
      <Dialog open={showDateModal} onOpenChange={setShowDateModal}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Selecionar Intervalo de Datas</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Data Inicial</Label>
              <CalendarComponent
                mode="single"
                selected={dateRange.from}
                onSelect={(date) => {
                  setDateRange((prev) => ({ ...prev, from: date }));
                  if (date) setFromMonth(date);
                }}
                month={fromMonth}
                onMonthChange={setFromMonth}
                locale={ptBR}
                className="rounded-md border"
              />
            </div>
            <div className="space-y-2">
              <Label>Data Final</Label>
              <CalendarComponent
                mode="single"
                selected={dateRange.to}
                onSelect={(date) => {
                  setDateRange((prev) => ({ ...prev, to: date }));
                  if (date) setToMonth(date);
                }}
                month={toMonth}
                onMonthChange={setToMonth}
                locale={ptBR}
                className="rounded-md border"
                disabled={(date) => dateRange.from ? date < dateRange.from : false}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDateModal(false);
                setPeriodo("7d");
                setDateRange({});
                setAppliedDateRange({});
                setFromMonth(new Date());
                setToMonth(new Date());
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => {
                if (dateRange.from && dateRange.to) {
                  setAppliedDateRange({ from: dateRange.from, to: dateRange.to });
                  setShowDateModal(false);
                  setPeriodo("custom");
                }
              }}
              disabled={!dateRange.from || !dateRange.to}
            >
              Aplicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Seleção de Ano */}
      <Dialog
        open={showYearModal}
        onOpenChange={(open) => {
          setShowYearModal(open);
          if (!open && !appliedAno) {
            setPeriodo("7d");
            setAno(undefined);
          }
        }}
      >
        <DialogContent className="sm:max-w-[325px]">
          <DialogHeader>
            <DialogTitle>Selecionar Ano</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="ano-input-graphs">Ano</Label>
              <Input
                id="ano-input-graphs"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="Ex: 2024"
                value={ano ?? ""}
                onChange={(e) => {
                  const value = e.target.value;
                  const parsed = value === "" ? undefined : parseInt(value, 10);
                  setAno(Number.isNaN(parsed) ? undefined : parsed);
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowYearModal(false);
                setPeriodo("7d");
                setAno(undefined);
                setAppliedAno(undefined);
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => {
                if (ano) {
                  setAppliedAno(ano);
                  setShowYearModal(false);
                  setPeriodo("Anual");
                }
              }}
              disabled={!ano}
            >
              Aplicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
